/**
 * useTasks hook -- fetches tasks from GET /tasks and provides mutation methods.
 */

import { useState, useCallback, useEffect } from "react";
import { apiClient } from "../lib/api-client";
import type { Task } from "@atlas/shared";

/**
 * Input for creating a task. Only `title` is required; the backend applies
 * Zod defaults for status, priority, tags, etc.
 */
export interface CreateTaskInput {
  title: string;
  description?: string;
  status?: string;
  priority?: string;
  projectId?: string;
  areaId?: string;
  tags?: string[];
  dueDate?: string;
  dueType?: string;
  scheduledDate?: string;
  estimatedMinutes?: number;
  effort?: string;
  aiNotes?: string;
  consequenceOfDelay?: string;
}

export interface UseTasksOptions {
  status?: string;
  includeDone?: boolean;
}

export interface UseTasksReturn {
  tasks: Task[];
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  createTask: (input: CreateTaskInput) => Promise<Task>;
  updateTask: (id: string, updates: Partial<CreateTaskInput>) => Promise<Task>;
  deleteTask: (id: string) => Promise<void>;
}

interface TasksResponse {
  tasks: Task[];
}

interface TaskResponse {
  task: Task;
}

function buildTasksPath(status?: string, includeDone?: boolean): string {
  const params = new URLSearchParams();
  if (status) params.set("status", status);
  if (includeDone) params.set("includeDone", "true");
  const qs = params.toString();
  return qs ? `/tasks?${qs}` : "/tasks";
}

export function useTasks(apiBaseUrl: string, options?: UseTasksOptions): UseTasksReturn {
  const { status, includeDone } = options ?? {};
  const [tasks, setTasks] = useState<Task[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const path = buildTasksPath(status, includeDone);
      const data = await apiClient<TasksResponse>(apiBaseUrl, path);
      setTasks(data.tasks);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsLoading(false);
    }
  }, [apiBaseUrl, status, includeDone]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setIsLoading(true);
      setError(null);

      try {
        const path = buildTasksPath(status, includeDone);
        const data = await apiClient<TasksResponse>(apiBaseUrl, path);
        if (!cancelled) {
          setTasks(data.tasks);
        }
      } catch (err) {
        if (!cancelled) {
          setError((err as Error).message);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, status, includeDone]);

  const createTask = useCallback(async (input: CreateTaskInput): Promise<Task> => {
    const data = await apiClient<TaskResponse>(apiBaseUrl, "/tasks", {
      method: "POST",
      body: JSON.stringify(input),
    });
    await refresh();
    return data.task;
  }, [apiBaseUrl, refresh]);

  const updateTask = useCallback(async (
    id: string,
    updates: Partial<CreateTaskInput>,
  ): Promise<Task> => {
    const data = await apiClient<TaskResponse>(apiBaseUrl, `/tasks/${id}`, {
      method: "PUT",
      body: JSON.stringify(updates),
    });
    await refresh();
    return data.task;
  }, [apiBaseUrl, refresh]);

  const deleteTask = useCallback(async (id: string): Promise<void> => {
    await apiClient(apiBaseUrl, `/tasks/${id}`, {
      method: "DELETE",
    });
    await refresh();
  }, [apiBaseUrl, refresh]);

  return { tasks, isLoading, error, refresh, createTask, updateTask, deleteTask };
}
